/**
 * Bill File Search API
 * 
 * Wrapper for searching bill files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIBillFileSearchResponse } from '../../../generated-types/models/APIBillFileSearchResponse'
import type { APIBillFileSearchResult } from '../../../generated-types/models/APIBillFileSearchResult'
import type { V1SearchContractsBillFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface BillFileSearchFilters {
  q: string
  billId?: number
  contractId?: number
  requestId?: number
  requestCertificate?: string
  fileName?: string
  storedAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface BillFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toBillFileSearchRequest(
  filters: BillFileSearchFilters
): V1SearchContractsBillFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    billId: filters.billId,
    contractId: filters.contractId,
    requestId: filters.requestId,
    requestCertificate: filters.requestCertificate,
    fileName: filters.fileName,
    storedAt: filters.storedAt ? new Date(filters.storedAt) : undefined,
    fileSize: filters.fileSize,
  }
}

/**
 * Search for bill files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Bill file search response with results and pagination info
 */
export async function searchBillFiles(
  filters: BillFileSearchFilters
): Promise<BillFileSearchResponse> {
  const response: APIBillFileSearchResponse = await v1Api.v1SearchContractsBillFilesRetrieve(
    toBillFileSearchRequest(filters)
  )

  // Map results to FileInfo
  const fileInfos = response.results.map((result) => {
    const fileInfo: Record<string, unknown> = {
      self: `/v1/contract/bill/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      size: result.fileSize ?? 0,
      stored_at: result.storedAt?.toISOString(),
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.billId) {
      fileInfo.bill = `/v1/contract/bills/${result.billId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
